import { useCallback, useEffect, useMemo, useState } from "react";
import leadService from "../services/lead.service";
import { errorToast } from "../../../react-toastfiy/toast";
import stateService from "../../../services/state.service";
import Swal from "sweetalert2";
import jwtDecode from "jwt-decode";

function useLeadManagement() {
  const initialValue = useMemo(() => {
    return {
      lead_id: "",
      name: "",
      phone: "",
      state: "",
      city: "",
      plan_interest: "",
      ta_interest: false,
      subscription_interest: true,
      subscription_avail_date: "",
      not_interested_reason: "",
      is_lead_closed: false,
      follow_up_date: "",
      remark: "",
      segment: {
        "2W": false,
        "3W": false,
        Car: false,
        Tractor: false,
        CV: false,
        All: false,
      },
    };
  }, []);

  const token = localStorage.getItem("refreshToken");
  const user = jwtDecode(token);

  const [leadSourceOptions, setLeadSourceOptions] = useState({ All: 0 });
  const [leadSource, setLeadSource] = useState({ option: "All", count: 0 });
  const [leadList, setLeadList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refetch, setRefetch] = useState(true);
  // Seed from the same persisted key ButtonTabBar uses ("leadsListTabId") so the
  // queue we fetch matches the tab that renders active on landing. Without this,
  // the bar restores e.g. "Follow Ups" while the data defaults to "new".
  const [leadType, setLeadType] = useState(() => {
    const saved = localStorage.getItem("leadsListTabId");
    return ["new", "followups", "closed"].includes(saved) ? saved : "new";
  });
  const [modalData, setModalData] = useState(initialValue);
  const [isEdit, setIsEdit] = useState(false);
  const [filteredData, setFilteredData] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [statesList, setStatesList] = useState([]);
  const [showRemarksModal, setShowRemarksModal] = useState(false);
  const [loadingRemarks, setLoadingRemarks] = useState(false);
  const [remarksList, setRemarksList] = useState([]);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [leadCounts, setLeadCounts] = useState({
    new: 0,
    overdue: 0,
    today: 0,
    upcoming: 0,
    unscheduled: 0,
    closed: 0,
  });
  const [countsLoading, setCountsLoading] = useState(true);
  // Overlap detection for the Add-Lead form: result of the phone lookup, and
  // whether the enterer has chosen to take ownership of a claimable existing lead.
  const [existingLead, setExistingLead] = useState(null);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [claiming, setClaiming] = useState(false);

  // One-at-a-time phone reveal (table screen-privacy) — holding only a
  // single {leadId, phone} pair means revealing a new row's number
  // automatically un-reveals whichever one was shown before.
  const [revealedPhone, setRevealedPhone] = useState(null);
  const [revealingPhoneLeadId, setRevealingPhoneLeadId] = useState(null);

  const handleRevealPhone = useCallback(async (leadId) => {
    setRevealingPhoneLeadId(leadId);
    const response = await leadService.revealLeadPhone(leadId);
    setRevealingPhoneLeadId(null);
    if (response?.success) {
      setRevealedPhone({ leadId, phone: response.phone });
    } else {
      errorToast(response?.error || "Failed to reveal phone number");
    }
  }, []);

  const handleChange = useCallback((e) => {
    let { name, value, type } = e.target;
    if (type === "radio") {
      value = e.target.value === "true";
    }
    if (
      type === "checkbox" &&
      (name === "ta_interest" || name === "is_lead_closed")
    ) {
      value = e.target.checked;
    }
    setModalData((prevData) => {
      if (name === "segment") {
        const segmentValue = e.target.value;
        if (segmentValue === "All") {
          const allSegmentsSelected = !prevData.segment["All"];
          return {
            ...prevData,
            segment: {
              "2W": allSegmentsSelected,
              "3W": allSegmentsSelected,
              Car: allSegmentsSelected,
              Tractor: allSegmentsSelected,
              CV: allSegmentsSelected,
              All: allSegmentsSelected,
            },
          };
        } else {
          const prev = structuredClone(prevData);
          delete prev.segment.All;
          delete prev.segment[segmentValue];
          const allSelected =
            Object.values(prev.segment).every((value) => value) &&
            !prevData.segment[segmentValue];

          return {
            ...prevData,
            segment: {
              ...prevData.segment,
              [segmentValue]: !prevData.segment[segmentValue],
              All: allSelected,
            },
          };
        }
      }
      return {
        ...prevData,
        is_lead_closed: type === "radio" ? !value : prevData.is_lead_closed,
        [name]: value,
      };
    });
  }, []);

  const handleLeadSourceChange = useCallback(
    ({ option, count }) => {
      setLeadSource({ option, count });
      if (option === "All") {
        setFilteredData(leadList);
      } else {
        const filteredLeads = leadList.filter(
          (lead) => lead?.inquiry?.inquiry_type === option,
        );
        setFilteredData(filteredLeads);
      }
    },
    [leadList],
  );

  // Debounced overlap lookup: when a full 10-digit phone is entered on the Add
  // form, check whether a lead already exists and whether it can be taken over.
  useEffect(() => {
    if (isEdit) return; // editing an existing lead — no overlap check
    const phone = (modalData.phone || "").toString()?.replace(/\D/g, "");
    if (phone.length !== 10) {
      setExistingLead(null);
      setClaiming(false);
      setLookupLoading(false);
      return;
    }
    let cancelled = false;
    setLookupLoading(true);
    const timer = setTimeout(async () => {
      const res = await leadService.lookupLead(phone);
      if (cancelled) return;
      setLookupLoading(false);
      if (res?.success && res.status !== "none") {
        setExistingLead(res);
      } else {
        setExistingLead(null);
      }
      setClaiming(false);
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [modalData.phone, isEdit, showModal]);

  // Enterer chooses to take over a claimable existing lead: pull in the identity
  // fields (name/state/city) so the form is valid; interest fields stay blank.
  const handleTakeOwnership = useCallback(() => {
    if (!existingLead || existingLead.status !== "claimable") return;
    setClaiming(true);
    setModalData((prev) => ({
      ...prev,
      lead_id: existingLead.lead_id,
      name: existingLead.name || prev.name,
      state: existingLead.state || prev.state,
      city: existingLead.city || prev.city,
    }));
  }, [existingLead]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSubmitLoading(true);
      const form_data = new FormData();
      for (const key in modalData) {
        if (!["", null, undefined].includes(modalData[key])) {
          if (key === "segment") {
            const segmentsObject = structuredClone(modalData[key]);
            delete segmentsObject.All;
            const segments = Object.keys(segmentsObject).filter(
              (segment) => segmentsObject[segment] === true,
            );
            form_data.append(key, JSON.stringify(segments));
          } else if (
            key === "phone" &&
            isEdit &&
            !/^\d+$/.test(modalData.phone || "")
          ) {
            // A phone containing non-digits is the masked placeholder the
            // backend sent because this user can't see the real number —
            // never resubmit it, or it overwrites the real phone with the
            // mask itself.
          } else {
            form_data.append(key, modalData[key]);
          }
        }
      }
      if (isEdit) {
        const response = await leadService.updateLead(form_data);
        if (response?.success) {
          setSubmitLoading(false);
          setRefetch(true);
          setModalData(initialValue);
          setShowModal(false);
        } else {
          setSubmitLoading(false);
          errorToast(response?.error);
          setError("Failed to update lead");
        }
      } else if (claiming && modalData.lead_id) {
        // Taking over a claimable existing lead rather than creating a new one.
        const response = await leadService.claimLead(form_data);
        if (response?.success) {
          setSubmitLoading(false);
          setRefetch(true);
          setModalData(initialValue);
          setExistingLead(null);
          setClaiming(false);
          setShowModal(false);
        } else {
          errorToast(response?.error);
          setSubmitLoading(false);
          setError("Failed to claim lead");
        }
      } else {
        // Leads entered here are offline/field leads — state the source explicitly
        // so it doesn't depend on backend role-branching.
        form_data.append("inquiry_type", "SH Offline Lead");
        const response = await leadService.createLead(form_data);
        if (response?.success) {
          setSubmitLoading(false);
          setRefetch(true);
          setModalData(initialValue);
          setExistingLead(null);
          setShowModal(false);
        } else {
          errorToast(response?.error);
          setSubmitLoading(false);
          setError("Failed to create lead");
        }
      }
    } catch (error) {
      setSubmitLoading(false);
      setError(error);
    }
  };

  const handleRemarks = useCallback(
    async (leadId) => {
      try {
        setShowRemarksModal(true);
        setLoadingRemarks(true);

        // lead_id, not phone — the row's phone may be the masked placeholder
        // for restricted accounts, and the backend resolves the real phone
        // from lead_id itself.
        const form_data = new FormData();
        form_data.append("lead_id", leadId);

        const response = await leadService.getComments(form_data);
        if (response?.success) {
          setLoadingRemarks(false);
          setRemarksList(response?.comments);
        } else {
          setLoadingRemarks(false);
          errorToast(response?.error);
          setError("Failed to fetch remarks");
        }
      } catch (error) {
        console.error("Error fetching remarks:", error);
        setLoadingRemarks(false);
      }
    },
    [setShowRemarksModal, setLoadingRemarks, setRemarksList],
  );

  const handleCloseLead = useCallback(
    async (lead_id) => {
      try {
        Swal.fire({
          title: "Are you sure?",
          text: "You won't be able to revert this!",
          icon: "warning",
          showCancelButton: true,
          confirmButtonColor: "#3085d6",
          cancelButtonColor: "#d33",
          confirmButtonText: "Yes, Close it!",
          showLoaderOnConfirm: true,
          preConfirm: async () => {
            const form_data = new FormData();
            form_data.append("lead_id", lead_id);
            form_data.append("is_lead_closed", true);

            const response = await leadService.updateLead(form_data);
            if (response?.success) {
              Swal.fire(
                "Success!",
                "The lead has been closed successfully.",
                "success",
              ).then((result) => {
                if (result.isConfirmed) {
                  setRefetch(true);
                }
              });
            } else {
              errorToast(response?.error);
              setError("Failed to close lead");
            }
          },
        });
      } catch (error) {
        setError(error);
      }
    },
    [setRefetch],
  );

  const handleEditLead = useCallback(
    (lead) => {
      const segment = {};
      const defaultSegments = ["2W", "3W", "Car", "CV", "Tractor"];

      defaultSegments.forEach((item) => {
        if (lead?.segment?.includes(item)) {
          segment[item] = true;
        } else {
          segment[item] = false;
        }
      });

      segment["All"] = defaultSegments.every((item) => segment[item]);

      const leadData = {
        ...initialValue,
        name: lead?.inquiry?.name,
        phone: lead?.inquiry?.phone,
        city: lead?.inquiry?.city,
        state: lead?.inquiry?.state,
        lead_id: lead?.lead_id,
        plan_interest: lead?.plan_interest,
        ta_interest: lead?.ta_interest,
        subscription_interest: lead?.subscription_interest,
        follow_up_date: lead?.follow_up_date ? lead?.follow_up_date : "",
        remark: lead?.remark ? lead?.remark : "",
        not_interested_reason: lead?.not_interested_reason
          ? lead?.not_interested_reason
          : "",
        create_by_name: lead?.create_by_name,
        create_by_phone: lead?.create_by_phone,
        lead_source: lead?.lead_source,
        is_lead_closed: lead?.is_lead_closed,
        segment: segment,
      };
      setModalData(leadData);
      setShowModal(true);
      setIsEdit(true);
    },
    [initialValue, setModalData, setShowModal, setIsEdit],
  );

  useEffect(() => {
    const fetchLeadList = async () => {
      try {
        setLoading(true);
        setError(null);
        setRefetch(false);
        setLeadList([]);
        leadService.getLeads(leadType).then((response) => {
          if (response?.success) {
            setLeadList(response?.leads);
            setFilteredData(response?.leads);

            const leadSourceCount = {
              All: response?.leads?.length,
              ...response?.leads?.reduce((acc, lead) => {
                const source = lead?.inquiry?.inquiry_type;
                acc[source] = (acc[source] || 0) + 1;
                return acc;
              }, {}),
            };
            setLeadSource({
              option: "All",
              count: leadSourceCount["All"],
            });

            setLeadSourceOptions(leadSourceCount);
            setLoading(false);
          } else {
            errorToast(response?.error);
            setError("Failed to fetch lead list");
            setLoading(false);
          }
        });
      } catch (error) {
        setLoading(false);
        setError(error);
      }
    };

    const fetchLeadCount = async () => {
      try {
        setCountsLoading(true);
        setError(null);
        const response = await leadService.getLeadsCount();

        if (response?.success) {
          setLeadCounts({
            new: response?.new ?? 0,
            overdue: response?.overdue ?? 0,
            today: response?.today ?? 0,
            upcoming: response?.upcoming ?? 0,
            unscheduled: response?.unscheduled ?? 0,
            closed: response?.closed ?? 0,
          });
          setCountsLoading(false);
        } else {
          setCountsLoading(false);
          errorToast(response?.error);
          setError("Failed to fetch lead count");
        }
      } catch (error) {
        setCountsLoading(false);
        setError(error);
      }
    };

    if (refetch) {
      fetchLeadList();
      fetchLeadCount();
    }
  }, [leadType, refetch]);

  useEffect(() => {
    const fetchStatesList = async () => {
      try {
        setError(null);
        const formData = new FormData();
        formData.append("phone", user?.phone);
        const response = await stateService.getUserStatePermissions(formData);
        if (response?.status === 200) {
          setStatesList(response?.data);
        } else {
          errorToast(response?.error);
        }
      } catch (error) {
        setError(error);
      }
    };
    fetchStatesList();
  }, []);

  // useEffect(() => {
  //   const leadSourceCount = {
  //     All: filteredData?.length,
  //     ...filteredData?.reduce((acc, lead) => {
  //       const source = lead?.inquiry?.inquiry_type;
  //       acc[source] = (acc[source] || 0) + 1;
  //       return acc;
  //     }, {}),
  //   };
  //   setLeadSource({
  //     option: "All",
  //     count: leadSourceCount["All"],
  //   });

  //   setLeadSourceOptions(leadSourceCount);
  // }, [filteredData]);

  // Primary tabs count (Follow-ups = overdue + today + upcoming + unscheduled).
  const followupsTotal =
    leadCounts.overdue +
    leadCounts.today +
    leadCounts.upcoming +
    leadCounts.unscheduled;
  const tabsCount = {
    new: leadCounts.new,
    followups: followupsTotal,
    closed: leadCounts.closed,
  };

  const handleSourceFilter = useCallback(() => {
    if (leadSource?.option === "All") {
      return leadList;
    } else {
      const filteredLeads = leadList.filter(
        (lead) => lead?.inquiry?.inquiry_type === leadSource?.option,
      );
      return filteredLeads;
    }
  }, [leadList, leadSource]);

  return {
    leadList,
    loading,
    error,
    handleChange,
    setRefetch,
    handleSubmit,
    setLeadType,
    setModalData,
    setIsEdit,
    setShowModal,
    showModal,
    modalData,
    initialValue,
    setFilteredData,
    isEdit,
    filteredData,
    statesList,
    showRemarksModal,
    setShowRemarksModal,
    handleRemarks,
    loadingRemarks,
    remarksList,
    submitLoading,
    leadType,
    tabsCount,
    leadCounts,
    handleCloseLead,
    leadSourceOptions,
    leadSource,
    handleLeadSourceChange,
    handleSourceFilter,
    handleEditLead,
    existingLead,
    lookupLoading,
    claiming,
    handleTakeOwnership,

    revealedPhone,
    revealingPhoneLeadId,
    handleRevealPhone,
  };
}

export default useLeadManagement;
